import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { Toast, ToastActionButton } from '@socialchorus/shared-ui-components';

/**
 * Imperative bridge for the Solar (Radix-based) Toast component.
 *
 * The Solar `Toast` from `@socialchorus/shared-ui-components` is declarative —
 * you render a `<Toast>` and drive its `open`/`onOpenChange` yourself. Most of
 * our callers (e.g. the UGC composer) fire a toast from an async success
 * callback and then navigate away, so the toast must outlive the component that
 * triggered it. This provider keeps the active toasts in a single place mounted
 * high in the tree (inside the Radix `ToastProvider` in `patron.jsx`) and
 * exposes a `showToast` function so callers can fire-and-forget.
 *
 * Replaces the deprecated recoil `Toaster`/`usePushToastOrFlashMessage`.
 */

export type SolarToastAction = {
  /** Visible label on the action button. */
  label: string;
  /**
   * Accessible alternative text describing the action for screen-reader users
   * (Radix requires this on toast actions). Defaults to `label`.
   */
  altText?: string;
  onClick: () => void;
};

export type ShowToastOptions = {
  title: string;
  variant?: 'success' | 'error';
  iconName?: string;
  action?: SolarToastAction;
  /** Auto-dismiss duration in ms. Omit to inherit the provider default. */
  duration?: number;
};

type ActiveToast = ShowToastOptions & { id: number };

type SolarToastContextValue = {
  showToast: (options: ShowToastOptions) => void;
};

const SolarToastContext = createContext<SolarToastContextValue | null>(null);

export const SolarToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const showToast = useCallback((options: ShowToastOptions) => {
    nextId.current += 1;
    const id = nextId.current;
    setToasts((current) => [...current, { ...options, id }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <SolarToastContext.Provider value={value}>
      {children}
      {toasts.map(({ id, title, variant, iconName, action, duration }) => (
        <Toast
          key={id}
          title={title}
          variant={variant}
          iconName={iconName}
          duration={duration}
          defaultOpen
          onOpenChange={(open) => {
            if (!open) removeToast(id);
          }}
          actionButton={
            action ? (
              <ToastActionButton
                altText={action.altText ?? action.label}
                label={action.label}
                onClick={action.onClick}
              />
            ) : undefined
          }
        />
      ))}
    </SolarToastContext.Provider>
  );
};

export const useSolarToast = (): SolarToastContextValue => {
  const context = useContext(SolarToastContext);
  if (!context) {
    throw new Error('useSolarToast must be used within a SolarToastProvider');
  }
  return context;
};
